import React, { useCallback, useRef } from 'react';
import { BoxProps, IconButton } from '@withjoy/joykit';
import { Search, Close } from '@withjoy/joykit/icons';
import { iconButtonOverrides, StyledEndEnhancementContainer, StyledInput, StyledSearchInputBarRoot, styles } from './SearchInputBar.styles';
import { UseSearchBarReturn } from '../../hooks/useSearchBar';
import { useTranslation } from '@shared/core';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';

interface SearchInputBarProps
  extends Readonly<
    {
      placeholder?: string;
    } & Omit<
      UseSearchBarReturn,
      'isSearchOpen' | 'handleOnClose' | 'handleOnOpen' | 'inputRef' | 'isOpenEditGiftDialog' | 'onCloseEditGiftDialog' | 'handleOnSelectOptionSearchQuery'
    > &
      Omit<BoxProps, '__css'>
  > {}

export const SearchInputBar = (props: SearchInputBarProps) => {
  const {
    placeholder: placeholderProp,
    clearSearchText,
    handleOnChange,
    handleOnFocus,
    handleOnBlur,
    handleOnKeyDown,
    handleOnSubmit,
    searchText,
    handleOnPasteSearch,
    ...restProps
  } = props;
  const { t, tq } = useTranslation('sharedRegistry');
  const { shortPlaceholder } = t('catalogSearchInput');
  const placeholder = placeholderProp ?? tq`Search brands, products, and more`;
  const isMobile = useIsMobileScreen();
  const ref = useRef<HTMLInputElement>(null);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLFormElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        ref.current?.blur();
        // Change submit default event to KeyboardEvent for telemetry purposes
        handleOnSubmit(event);
      }
    },
    [handleOnSubmit],
  );

  return (
    <StyledSearchInputBarRoot
      as="form"
      action="."
      onKeyDown={handleKeyDown}
      onSubmit={handleOnSubmit}
      __css={styles.root}
      {...restProps}
    >
      <StyledEndEnhancementContainer __css={styles.endEnhancementContainer(false)}>
        {isMobile && (
          <IconButton
            key="search"
            aria-label={`Search for`}
            type="submit"
            shape="rounded"
            color="mono14"
            _hover={{ color: 'mono13', backgroundColor: '#FFF' }}
            _active={{ color: 'mono14', backgroundColor: '#FFF' }}
            backgroundColor="#FFF"
            size={'sm'}
          >
            <Search size={24} />
          </IconButton>
        )}
      </StyledEndEnhancementContainer>
      <StyledInput
        as="input"
        type="search"
        __css={styles.input}
        placeholder={isMobile ? shortPlaceholder() : placeholder}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        value={searchText}
        onChange={handleOnChange}
        onKeyDown={handleOnKeyDown}
        onPaste={handleOnPasteSearch}
        ref={ref}
      />
      <StyledEndEnhancementContainer __css={styles.endEnhancementContainer(true)}>
        {searchText && (
          <IconButton
            key="clear"
            aria-label={`Clear search`}
            shape="rounded"
            size="sm"
            backgroundColor="mono4"
            color="mono14"
            marginRight={5}
            height={22}
            width={22}
            minWidth="unset"
            _hover={{ backgroundColor: 'mono5' }}
            _active={{ backgroundColor: 'mono7' }}
            onClick={clearSearchText}
          >
            <Close size={16} />
          </IconButton>
        )}
        {!isMobile && (
          <IconButton
            key="search"
            aria-label={`Search for`}
            type="submit"
            shape="rounded"
            size={'sm'}
            {...iconButtonOverrides}
          >
            <Search size={24} />
          </IconButton>
        )}
      </StyledEndEnhancementContainer>
    </StyledSearchInputBarRoot>
  );
};
